import { useCallback, useMemo } from "react";

import type { ConditionBuild, ConditionSlotId, EffectBuild } from "@skilitics/nautilus-core-designer-data";

import { useHandlerDesigner } from "./HandlerDesignerContext";
import { resolveAddableConditionBuildablesCreate } from "./querying";
import { ResponseByIdContext } from "./ResponseByIdContext";
import { useResponseDetailsDesigner } from "./ResponseDesignerContext";

type ProviderProps = {
  responseId: number;
  children: Exclude<React.ReactNode, null | undefined | boolean>;
};

export const ResponseByIdProvider = ({ children, responseId }: ProviderProps) => {
  const rd = useResponseDetailsDesigner();
  const td = useHandlerDesigner();

  const { getEffectBuildable, createCondition, createEffect } = rd;

  const { nodeId, eventTypes } = td;

  // Use first eventType for buildables resolution (they're typically per-node, not per-event)
  const primaryEventType = eventTypes[0];

  const resolveEffectBuildables = useCallback(
    () => td.resolveEffectBuildables(nodeId, primaryEventType),
    [primaryEventType, nodeId, td],
  );

  const resolveAddableEffectBuildables = useCallback(
    () => rd.resolveAddableEffectBuildables(nodeId, primaryEventType, responseId),
    [primaryEventType, nodeId, rd, responseId],
  );

  const getEffects = useCallback(
    () => rd.getEffects(nodeId, primaryEventType, responseId),
    [primaryEventType, nodeId, rd, responseId],
  );

  const addEffect = useCallback((effect: EffectBuild) => rd.addEffect(responseId, effect), [rd, responseId]);

  const addRootCondition = useCallback(
    (condition: ConditionBuild) => rd.addCondition(condition, { responseId }),
    [rd, responseId],
  );

  const addCondition = useCallback(
    (condition: ConditionBuild, slotId: ConditionSlotId) => rd.addCondition(condition, { slotId }),
    [rd],
  );

  const resolveConditionBuildables = useCallback(() => td.resolveConditionBuildables(nodeId), [nodeId, td]);

  const resolveAddableConditionBuildables = useCallback(
    () => resolveAddableConditionBuildablesCreate(td.resolveConditionBuildables)(nodeId),
    [nodeId, td],
  );

  const getRootCondition = useCallback(() => rd.getCondition(nodeId, responseId), [nodeId, rd, responseId]);

  const {
    updateEffect,
    reorderEffect,
    removeEffect,
    moveCondition,
    removeCondition,
    reorderCondition,
    updateCondition,
  } = rd;

  const context = useMemo(
    (): ResponseByIdContext => ({
      responseId,
      createEffect,
      getEffectBuildable,
      addEffect,
      getEffects,
      updateEffect,
      reorderEffect,
      removeEffect,
      resolveEffectBuildables,
      resolveAddableEffectBuildables,

      addCondition,
      addRootCondition,
      createCondition,
      getRootCondition,
      moveCondition,
      removeCondition,
      reorderCondition,
      resolveConditionBuildables,
      resolveAddableConditionBuildables,
      updateCondition,
    }),
    [
      addCondition,
      addEffect,
      addRootCondition,
      createCondition,
      createEffect,
      getEffectBuildable,
      getEffects,
      getRootCondition,
      moveCondition,
      removeCondition,
      removeEffect,
      reorderCondition,
      reorderEffect,
      resolveAddableConditionBuildables,
      resolveAddableEffectBuildables,
      resolveConditionBuildables,
      resolveEffectBuildables,
      responseId,
      updateCondition,
      updateEffect,
    ],
  );

  return <ResponseByIdContext.Provider value={context}>{children}</ResponseByIdContext.Provider>;
};

ResponseByIdProvider.displayName = "ResponseByIdContext";
